/**
 * Act II — A Family Chosen (Genesis 12–50).
 *
 * After Babel, God stops working with humanity in general and starts working
 * with one household. Four chapters: the call of Abram, the mountain in
 * Moriah, Jacob at the Jabbok, and Joseph in Egypt.
 *
 * Scripture quoted inside scenes is the World English Bible (British Edition),
 * wrapped in *asterisks* for the serif face.
 */

export const ACT_FAMILY = {
  id: 'family',
  title: 'A Family Chosen',
  summary: 'God narrows his focus to one man, and stakes the world on a promise.',
  span: 'Genesis 12–50',
  chapters: [
    {
      id: 'ch-abram',
      title: 'Go',
      where: 'Genesis 12, 15',
      era: 'c. 2000 BC',
      artCaption: 'A tent pitched in someone else’s country, under more stars than anyone could count',
      scenes: [
        'Genesis stops talking about the human race and starts talking about one family. Terah leaves Ur with his son Abram, his daughter-in-law Sarai and his grandson Lot. They set out for Canaan. They get as far as Haran and settle there instead. Ur and Haran were both centres of moon worship, and Israel is told bluntly, much later, that their ancestors served other gods. Nothing about this household is promising. One fact is recorded before anything happens at all: *Sarai was barren. She had no child.* That is the ground the promise is planted in.',
        'Then God speaks. *Get out of your country, and from your relatives, and from your father’s house, to the land that I will show you.* He is not told which land. He is told to leave, and to start walking. Attached to the command is a promise in three parts: a nation, a name, a blessing. At Babel, people had set out to make a name for themselves, and it came apart. Abram is handed one instead. He is also told he is not the point of it.',
        'The promise ends with a line that puts the whole world back in view: *in you all the families of the earth will be blessed.* Babel scattered people. Abram is how they get gathered again. The cost is hard to feel now. In that world your family was your court, your insurance and your name all at once. A man without them had no standing anywhere. Abram walks about four hundred miles to a country that already has people living in it. He lives there in a tent for the rest of his life.',
        'Years pass and there is no child. When Abram finally raises it, God takes him outside and tells him to count the stars. Then he does something stranger. He has Abram cut three animals in half and lay the pieces out in two rows. That was how serious agreements were sworn. Both men walked the path between the halves, meaning: do this to me if I break my word. Abram falls into a deep sleep. A smoking firepot and a flaming torch pass between the pieces without him. Only one side walks.',
        'Between the stars and the animals sits one sentence. Genesis 15:6: *He believed in the LORD, who credited it to him for righteousness.* Nothing has been achieved by that point. There is no son, no land, no temple. There is no law to keep either — the law is four hundred years away. A man believes an unlikely promise, and is counted as being in the right with God. Paul builds an argument on the order of events. The verdict lands before Abram is circumcised, and long before he climbs the mountain with Isaac.',
        'It does not tidy up. Abram tries to help the promise along and has a son, Ishmael, by Sarai’s slave Hagar, and the fallout runs through the rest of Genesis. At ninety-nine he is renamed Abraham, father of a multitude, which for a man with no son by his wife is either faith or a joke. Isaac is born when he is a hundred. And when Abraham dies, the only piece of the promised land he owns is a burial cave near Hebron, bought from a local farmer for four hundred shekels of silver.',
      ],
      verseId: 'gen-12-2',
      takeaway: 'The promise came before the family, and Abram was counted righteous for trusting it.',
      questions: [
        {
          id: 'sq-abram-1',
          question: 'What was Abram told about the land he was being sent to?',
          options: [
            'That it was Egypt',
            'That it lay seven days to the east',
            'Nothing beyond “a land that I will show you”',
            'That he would be back within a year',
          ],
          answer: 2,
          explanation:
            'Genesis 12:1. The country is not named until he is standing in it, which is why Hebrews 11:8 describes him going out “not knowing where he went”.',
        },
        {
          id: 'sq-abram-2',
          question: 'In the covenant ceremony of Genesis 15, who passed between the halves of the animals?',
          options: [
            'Abram alone',
            'God alone, as a smoking firepot and a flaming torch',
            'Abram and God together',
            'Neither — the animals were burnt whole',
          ],
          answer: 1,
          explanation:
            'Both parties normally walked that path, invoking the animals’ fate on themselves if they broke the agreement. Here only one party walks, and it is not the human one.',
        },
        {
          id: 'sq-abram-3',
          question: 'What does Genesis 15:6 say was credited to Abram as righteousness?',
          options: [
            'The journey he had already made',
            'His circumcision',
            'His willingness to offer Isaac',
            'His belief in what God had promised',
          ],
          answer: 3,
          explanation:
            'Paul turns the sequence into his argument: the verdict is passed two chapters before circumcision and seven before the mountain, so it cannot have been earned by either.',
        },
        {
          id: 'sq-abram-4',
          question: 'How much of the promised land did Abraham own by the time he died?',
          options: [
            'A burial cave he paid for',
            'The town of Hebron',
            'A day’s ride around Beersheba',
            'Roughly a third of Canaan',
          ],
          answer: 0,
          explanation:
            'Genesis 23 — four hundred shekels of silver for a cave and the field around it. The man promised a nation dies holding the deeds to a grave.',
        },
      ],
    },
    {
      id: 'ch-isaac',
      title: 'The Mountain',
      where: 'Genesis 22',
      era: 'c. 1880 BC',
      artCaption: "The wood, the altar, and the ram caught in the thicket",
      scenes: [
        'Isaac arrives twenty-five years after the promise. By then both his parents have laughed at the idea — Abraham face down on the ground, Sarah listening from behind a tent flap. So they name him Isaac, which means he laughs. Sarah’s line at his birth is one of the warmest in Genesis. *God has made me laugh. Everyone who hears will laugh with me.* Then the whole covenant closes around one small boy. The nation, the land, the blessing on every family on earth — all of it now has to travel through him.',
        'Then God asks for him. *Now take your son, your only son, whom you love, even Isaac, and go into the land of Moriah.* That is the first time the word love appears in the Hebrew Bible. It appears in a sentence about handing the loved thing over. God gives the same command he gave the first time, back in chapter 12: go. Abraham is being sent out all over again. Again he is not told exactly where he is going.',
        'It is a three days’ walk, and Genesis says nothing at all about those three days. The request is horrifying, but it was not unthinkable there. The peoples around Abraham did sacrifice their children, and the law given later has to forbid it by name. Jeremiah says God never so much as thought of such a thing. So Abraham has been asked for something his neighbours would have understood. At the bottom of the hill he tells the servants that he and the boy will come back — both of them. Then he starts climbing.',
        'The boy carries the wood for his own offering, so he is not a toddler. He asks the only question available. The fire and the wood are here, *but where is the lamb for a burnt offering?* Abraham says God will provide the lamb himself. Whether that is faith or evasion, the story lets you decide. Then Abraham binds him, lays him on the wood, and reaches for the knife. Genesis reports it in a single verse and does not linger over it.',
        'A voice stops him. Only then does he look up and see a ram caught by its horns in a thicket, and he names the place *The LORD Will Provide* — in Hebrew, closer to: the LORD will see. 2 Chronicles says Solomon built the temple on Mount Moriah, the hill in Jerusalem, where Israel brought animals to die in their place for the next thousand years. The Gospel writers set the crucifixion just outside the same city, with a son carrying wood uphill. Christians have read the two side by side ever since.',
        'The ending is strange. Abraham comes back down, and the text mentions only him rejoining the servants. Isaac is not named again until a servant goes to find him a wife, two chapters later. God never speaks to Abraham again in Genesis. Decades of conversation stop on that summit. The next thing to happen is Sarah’s death, and a long haggle over a burial plot. The promise comes through intact. The household carrying it is much quieter afterwards, and the Bible never explains why.',
      ],
      verseId: 'gen-22-14',
      takeaway: 'Abraham trusted God past the point where the promise made sense.',
      questions: [
        {
          id: 'sq-isaac-1',
          question: 'What did Abraham name the place where the ram was provided?',
          options: [
            'The House of God',
            'The Place of Wrestling',
            'The LORD Is My Banner',
            'The LORD Will Provide',
          ],
          answer: 3,
          explanation:
            'Genesis 22:14 — in Hebrew, Yahweh Yireh. The verb underneath it means “to see”, and it runs through the chapter: God sees to it before Abraham sees the ram.',
        },
        {
          id: 'sq-isaac-2',
          question: 'What does the name Isaac mean?',
          options: [
            'God has heard',
            'He laughs',
            'He struggles with God',
            'Child of the promise',
          ],
          answer: 1,
          explanation:
            'Both parents laughed when the birth was announced, and Sarah says everyone who hears will laugh with her. “God has heard” is Ishmael; “he struggles with God” is Israel.',
        },
        {
          id: 'sq-isaac-3',
          question: 'Which later site does the Old Testament identify with Mount Moriah?',
          options: [
            'Mount Sinai',
            'Bethel',
            'The hill in Jerusalem where Solomon built the temple',
            'The plain of Jericho',
          ],
          answer: 2,
          explanation:
            '2 Chronicles 3:1. The place where a substitute was found for one boy became the place where a nation brought its substitutes for a thousand years.',
        },
      ],
    },
    {
      id: 'ch-jacob',
      title: 'Wrestling',
      where: 'Genesis 25–33',
      era: 'c. 1850–1820 BC',
      artCaption: 'The ford at the Jabbok, and a struggle that lasts until dawn',
      scenes: [
        'Rebekah has twins who fight before they are born, and an oracle that turns the usual order upside down: the elder will serve the younger. Esau arrives first, red and rough. Jacob follows with a hand on his brother’s heel, and is named for it. Jacob means heel-grabber, and it was not a compliment. He grows into it. He buys the birthright off a hungry brother for a bowl of red lentil stew. The birthright is the double share of the inheritance, and the headship of the family.',
        'The blessing is a separate thing from the birthright, and Jacob takes that too. Rebekah dresses him in Esau’s clothes and ties goatskins to his arms and neck. Blind old Isaac feels him and says the line the chapter turns on: the voice is Jacob’s voice, but the hands are the hands of Esau. Esau walks in minutes later and cries out. Jacob is sent away to save his life, and he never gets another scene with his mother. The Bible does not clean him up.',
        'He sleeps rough on the road north with a stone under his head, and dreams of a stairway between earth and sky with angels going up and down it. God stands there and repeats Abraham’s promise to him almost word for word: the land, the descendants, every family on earth blessed through them. It is handed to a man running from a fraud, who has earned none of it. Jacob’s reply is a bargain. If God keeps him and feeds him and brings him home, then the LORD will be his God.',
        'Twenty years in Haran, working for his uncle Laban. He works seven years for Rachel, and is handed her older sister Leah in the dark. Laban’s explanation is the sharpest sentence in Genesis: it is not done in our country to give the younger before the firstborn. The man who cheated his blind father by pretending to be the firstborn has just been cheated by a man quoting the rights of the firstborn. Jacob works seven more years, then six for his flocks. Laban changes his wages ten times.',
        'On the night he has to face the brother he cheated, he sends his family across the river Jabbok ahead of him and is left alone. Someone wrestles him until daybreak. Genesis never explains who. Jacob will only say afterwards that he saw God face to face. He loses — his hip is wrenched out of joint — and he wins, because he will not let go without a blessing. He gets one, and a new name: *Israel*, which means something like one who struggles with God.',
        'The sun comes up as he crosses over, and he is limping. Genesis adds that Israelites still do not eat the sinew of the hip, because of that night. It is kept in kosher butchery to this day. The nation that carries the promise is named after a man with a limp. When Esau finally appears, he runs and embraces him and weeps. Jacob presses gifts on him and calls them his blessing. He is handing back what he stole. What he never becomes is a good father, and the next generation goes badly wrong.',
      ],
      verseId: 'gen-32-28',
      takeaway: 'God works through people who are a mess, and renames them.',
      questions: [
        {
          id: 'sq-jacob-1',
          question: 'What did Esau give up for a bowl of red stew?',
          options: [
            'His father’s deathbed blessing',
            'His birthright as the firstborn',
            'His share of the family flocks',
            'His claim on his mother’s estate',
          ],
          answer: 1,
          explanation:
            'The birthright and the blessing are two different things — the double portion and headship first, the spoken blessing later. Jacob took both, in that order.',
        },
        {
          id: 'sq-jacob-2',
          question: 'How was Jacob himself deceived during his years in Haran?',
          options: [
            'Esau’s men ambushed him on the road',
            'His flocks were driven off in the night',
            'Laban gave him Leah instead of Rachel',
            'His wages were paid in worthless silver',
          ],
          answer: 2,
          explanation:
            'Laban justifies it by saying the younger is not given before the firstborn — an argument aimed squarely at a man who had taken the firstborn’s blessing by pretending to be his brother.',
        },
        {
          id: 'sq-jacob-3',
          question: 'What did Jacob take away from the night beside the Jabbok?',
          options: [
            'A written covenant with Esau',
            'Esau’s forgiveness, sent on ahead',
            'His birthright confirmed in law',
            'A new name and a permanent limp',
          ],
          answer: 3,
          explanation:
            'Israel means something like “one who struggles with God”. The blessing and the injury arrive in the same moment, and he carries both out of the river.',
        },
      ],
    },
    {
      id: 'ch-joseph',
      title: 'Sold',
      where: 'Genesis 37–50',
      era: 'c. 1800–1750 BC',
      artCaption: 'A robe, a dry pit, and the road down into Egypt',
      scenes: [
        'Jacob has twelve sons by four women, and makes no secret of which one he prefers. Nobody can translate the robe he gives Joseph with confidence. The famous coat of many colours comes from an old Greek guess; the Hebrew more likely means a long garment with sleeves. Either way the point holds. It is not work clothing. It marks out a seventeen-year-old who does not have to go out with the flocks. Joseph then tells his brothers about two dreams in which they all bow down to him. He is not tactful about it.',
        'They see him coming across the fields and decide to kill him. Reuben stalls. Judah suggests selling him instead. A caravan heading down to Egypt buys him for twenty pieces of silver, about the going rate for a slave. Then the brothers kill a goat, dip the robe in its blood, and let their father draw the obvious conclusion. Jacob once fooled his own blind father while wearing goatskins. Now he is fooled by a goat. Genesis does not pause to point this out.',
        'Egypt takes thirteen years off him. He is bought by an officer of Pharaoh’s guard and runs the household well. Then his master’s wife accuses him of something he did not do. He goes to prison. He reads a dream for a fellow prisoner, who is released and then forgets him for two years. He is seventeen when the pit closes over him, and thirty when he stands in front of Pharaoh. Unlike Abraham and Jacob, he never gets a vision or a voice. The narrator just keeps repeating that the LORD was with him.',
        'Pharaoh dreams about cattle and grain. None of his experts can read it, and the forgotten prisoner finally remembers. Joseph is shaved, given clean clothes and made second in the empire, with an Egyptian name and an Egyptian wife. Seven years of surplus are stored, and seven years of famine follow. How the rescue was paid for is rarely mentioned. Genesis 47 says the Egyptians bought grain first with money, then with their livestock, then with their land and themselves. Pharaoh ends up owning nearly everything. The narrator reports it without comment.',
        'Famine drives the brothers to Egypt, and they bow to a man they do not recognise. What follows is not simple forgiveness but a long, elaborate test, and Joseph keeps leaving the room to weep his way through it. He plants silver in their sacks, keeps one brother and demands another. Then he sets up the same situation as before: one favoured son of Rachel, and a clean chance to be rid of him. And Judah, whose idea the sale was, offers to be enslaved in Benjamin’s place. That is the family line David and Jesus come from.',
        'When Joseph tells them who he is, he says the same thing three times. God sent me ahead of you to keep people alive. Years later their father dies, and they are frightened of him. Then comes the sentence the book has been walking towards. *You meant evil against me, but God meant it for good.* He does not soften what they did. Joseph dies at a hundred and ten, having made them swear to carry his bones out when God brings them up. The last words of Genesis are a coffin in Egypt.',
      ],
      verseId: 'gen-50-20',
      takeaway: 'God can bend genuine evil towards good without calling it good.',
      questions: [
        {
          id: 'sq-joseph-1',
          question: 'How did the brothers account for Joseph’s disappearance to their father?',
          options: [
            'They said he had run away',
            'They said Egyptian traders had seized him',
            'They said nothing at all',
            'They sent back his robe, dipped in goat’s blood',
          ],
          answer: 3,
          explanation:
            'Jacob is deceived by a goat, having once deceived his own blind father while wearing goatskins. Genesis lets the echo sit there unmentioned.',
        },
        {
          id: 'sq-joseph-2',
          question: 'How long was it between Joseph being sold and standing before Pharaoh?',
          options: [
            'About two years',
            'Thirteen years',
            'Seven years',
            'Forty years',
          ],
          answer: 1,
          explanation:
            'He is seventeen at the pit and thirty before Pharaoh. Slavery, false accusation and prison are covered in roughly two pages, which flattens how long the waiting actually was.',
        },
        {
          id: 'sq-joseph-3',
          question: 'Which brother offers to be enslaved in Benjamin’s place?',
          options: [
            'Reuben',
            'Simeon',
            'Judah',
            'Levi',
          ],
          answer: 2,
          explanation:
            'Judah proposed selling Joseph in the first place. His offer to take a brother’s punishment is the quiet conversion in the story, and his is the line that runs to David.',
        },
        {
          id: 'sq-joseph-4',
          question: 'What does Joseph tell his brothers about what they did to him?',
          options: [
            'They meant it for evil, but God meant it for good',
            'That he had long since forgotten it',
            'That it was never really their fault',
            'That he would repay them in kind',
          ],
          answer: 0,
          explanation:
            'Genesis 50:20 holds two things at once without collapsing either: a real wrong that stays a wrong, and a God working through it anyway.',
        },
      ],
    },
  ],
};
